import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isBloggerBusiness } from '../../../utils/accessService';
import { buildAvatarImageSource } from '../../../utils/buildAvatarImageSource';
import { cropPublicId } from '../../../utils/cropPublicId';
import { useIsMobile } from '../../../utils/getIsMobile';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { Carousel } from '../../carousel/Carousel';
import { CarouselDotsIndicators } from '../../carousel/CarouselDotsIndicators/CarouselDotsIndicators';
import { useIsPostReported } from '../../hooks/useIsPostReported';
import { IconKebab } from '../../icon/IconKebab';
import { Box } from '../../layout/Box';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Flex } from '../../layout/Flex';
import { Avatar } from '../../media/Avatar';
import { PostCardStatus } from '../PostCardStatus/PostCardStatus';
import { makeMediaParams } from '../PostDetailsCard/utils';
import {
    AVATAR_SIZE,
    DEFAULT_ICON_PROPS,
    MENU_TRIGGER_SIZE,
    POST_GRID_CARD_FOOTER_HEIGHT,
} from './constants';
import { CardActions } from './parts/CardActions/CardActions';
import { CardMedia } from './parts/CardMedia/CardMedia';
import { ReportedPost } from './parts/ReportedPost/ReportedPost';
import { ViewsBadge } from './parts/ViewsBadge/ViewsBadge';
import { BusinessName, CardFooter, IconWrapper, RelativeContainer } from './styles';
import { PostCardGridProps } from './types';
import { useGridCardPlayer } from './useGridCardPlayer';
import { usePostGridCardKebab } from './usePostGridCardKebab';
import { getIconOffset, getPostCarGridIcons } from './utils';

export const PostCardGrid: FC<PostCardGridProps> = ({
    post,
    index,
    onReport,
    onGridRowShown,
    onClick,
    onBusinessClick,
    gridColumns,
    isVisible = false,
    addonAfter,
    shareDisabled,
    businessPageDisabled,
    imageSize,
    onBusinessPage,
    onContextMenuOpen,
    onShare,
    onVisibilityChange,
    onVideoPlay,
    onVideoStart,
    onVideoEnd,
    onVideoPause,
    onHover,
    onHoverCancel,
    onGridCardClick,
    onReaction,
    onBookmark,
    hideReportedPosts = true,
    showFooter = true,
    additionalIcon,
    isCompact,
    isShowStatus,
    actionsBottomOffset,
    hiddenActions,
}) => {
    const isMobile = useIsMobile();
    const isReported = useIsPostReported(post.id);

    const [currentSlide, setCurrentSlide] = useState(0);
    const isSliderEnabled = Number(useExperimentFlag('post_card_slider_enabled')) === 1;
    const isViewsBadgeEnabled = Number(useExperimentFlag('show_views_badge')) === 1;

    const videoContainerRef = useRef<HTMLDivElement | null>(null);
    const firstMedia = post.content[0]?.media?.[0];
    const ratio = Math.min((firstMedia?.width || 1) / (firstMedia?.height || 1), 1);

    const isFirstRenderRef = useRef(true);

    const icons = useMemo(() => getPostCarGridIcons(post), [post]);

    const handleReport = useCallback(() => {
        onReport?.();
    }, [onReport]);

    const redesignCardActions = Number(useExperimentFlag('post_card_actions_redesign')) === 1;

    const { kebab, openKebab } = usePostGridCardKebab({
        onReport: handleReport,
        onShare,
        onBusinessPage,
        onReaction,
        post,
        shareDisabled,
        businessPageDisabled,
        redesignCardActions,
    });

    const handleClick = useCallback(() => {
        onClick ? onClick({ postNumber: index }) : onGridCardClick({ postNumber: index });
    }, [onClick, index, onGridCardClick]);

    const handleBusinessClick = useCallback(() => {
        onBusinessClick ? onBusinessClick() : onBusinessPage?.(false);
    }, [onBusinessClick, onBusinessPage]);

    const handleOpenKebab = useCallback(
        (e: React.MouseEvent) => {
            openKebab(e);
            onContextMenuOpen?.();
        },
        [openKebab, onContextMenuOpen],
    );

    const { handleMouseOff, handleMouseOn, mediaCoverEnabled, spinnerEnabled } = useGridCardPlayer({
        content: post.content,
        containerRef: videoContainerRef,
        // Первый запуск видео
        onVideoStart: () => onVideoStart(makeMediaParams(post, 0)),
        // Не первый запуск видео
        onVideoPlay: () => onVideoPlay(makeMediaParams(post, 0)),
        onVideoEnd: () => onVideoEnd(makeMediaParams(post, 0)),
        onVideoPause: () => onVideoPause(makeMediaParams(post, 0)),
    });

    // Хак, чтобы вызывать onGridRowShown при первом рендере
    // isVisible обновится только после скролла
    useEffect(() => {
        if (index + 1 === gridColumns) {
            onGridRowShown?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onGridRowShown, gridColumns]);

    useEffect(() => {
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;

            return;
        }

        if (isVisible && (index + 1) % gridColumns === 0) {
            onGridRowShown?.();
        }

        onVisibilityChange({
            ...makeMediaParams(post, 0),
            isInViewport: isVisible,
            postNumber: index,
            isGrid: true,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, onGridRowShown, index, gridColumns, onVisibilityChange]);

    if (isReported && hideReportedPosts) {
        return <ReportedPost />;
    }

    if (isMobile === null) {
        return null;
    }

    const businessName = isBloggerBusiness(post.business?.type)
        ? post.business?.displayName
        : cropPublicId(post.business?.publicId || '');

    const displayName = businessName || post.ownersData?.author?.displayName;
    const avatarUrl =
        post.business?.avatarUrl || buildAvatarImageSource(post.ownersData?.author?.avatarUrl);

    return (
        <Box
            position="relative"
            onMouseEnter={onHover}
            onMouseLeave={onHoverCancel}
            dataTestId={DataTestId.POST_CARD_GRID}
        >
            {isShowStatus ? <PostCardStatus post={post} top={1} left={1} /> : null}
            {additionalIcon ? (
                <IconWrapper
                    key={index}
                    right={getIconOffset(0)}
                    top={1}
                    zIndex={1}
                    background={'none'}
                >
                    {additionalIcon}
                </IconWrapper>
            ) : null}
            {icons.map((Icon, index) => {
                const rightOffset = getIconOffset(additionalIcon ? index + 1 : index);

                return (
                    <IconWrapper
                        key={index}
                        right={rightOffset}
                        top={1}
                        zIndex={1}
                        background={redesignCardActions ? 'none' : undefined}
                    >
                        <Icon {...DEFAULT_ICON_PROPS} />
                    </IconWrapper>
                );
            })}
            <RelativeContainer>
                {isCompact ? (
                    <CardMedia
                        media={post.content[0]}
                        index={0}
                        ratio={ratio}
                        containerRef={videoContainerRef}
                        imageSize={imageSize}
                        mediaCoverEnabled={mediaCoverEnabled}
                        spinnerEnabled={spinnerEnabled}
                        onClick={handleClick}
                        handleMouseOn={handleMouseOn}
                        handleMouseOff={handleMouseOff}
                    />
                ) : (
                    <Carousel
                        count={post.content.length}
                        borderRadius={2}
                        overflow="hidden"
                        activeIndex={isSliderEnabled ? currentSlide : 0}
                        onSlideChange={isSliderEnabled ? setCurrentSlide : undefined}
                        type="hover"
                        renderItem={(index) => {
                            const media = post.content[index];

                            return (
                                <CardMedia
                                    key={index}
                                    media={media}
                                    index={index}
                                    ratio={ratio}
                                    containerRef={
                                        media.type === 'video' ? videoContainerRef : undefined
                                    }
                                    imageSize={imageSize}
                                    mediaCoverEnabled={mediaCoverEnabled}
                                    spinnerEnabled={spinnerEnabled}
                                    onClick={handleClick}
                                    handleMouseOn={handleMouseOn}
                                    handleMouseOff={handleMouseOff}
                                />
                            );
                        }}
                    />
                )}
                {!redesignCardActions && isViewsBadgeEnabled && post.publicStats?.shows ? (
                    <Box position="absolute" bottom="8px" left="8px">
                        <ViewsBadge views={post.publicStats.shows} />
                    </Box>
                ) : null}
                {redesignCardActions && !hiddenActions ? (
                    <CardActions
                        bottom={actionsBottomOffset}
                        post={post}
                        index={index}
                        onReaction={onReaction}
                        onBookmark={onBookmark}
                        onMouseEnter={isMobile ? undefined : handleMouseOn}
                        onMouseLeave={isMobile ? undefined : handleMouseOff}
                        onClick={handleClick}
                    />
                ) : null}
            </RelativeContainer>
            {addonAfter}
            <Flex
                position="absolute"
                bottom={`${POST_GRID_CARD_FOOTER_HEIGHT + 6}px`}
                left={0}
                width="100%"
                alignItems="center"
                justifyContent="center"
            >
                {isSliderEnabled ? (
                    <CarouselDotsIndicators
                        slidesCount={post.content.length}
                        activeIndex={currentSlide}
                    />
                ) : null}
            </Flex>
            {showFooter ? (
                <>
                    <CardFooter>
                        <Flex
                            direction="row"
                            alignItems="center"
                            onClick={handleBusinessClick}
                            cursor={onBusinessClick || onBusinessPage ? 'pointer' : 'default'}
                            width={`calc(100% - ${MENU_TRIGGER_SIZE}px)`}
                            spacing={1}
                        >
                            <Avatar
                                width={AVATAR_SIZE}
                                height={AVATAR_SIZE}
                                title={displayName}
                                src={avatarUrl}
                                border="0.5px solid var(--divider)"
                            />
                            <BusinessName>{displayName}</BusinessName>
                        </Flex>

                        <Box onClick={handleOpenKebab} cursor="pointer">
                            <IconKebab
                                color="var(--color-primary)"
                                fontSize={MENU_TRIGGER_SIZE}
                                dataTestId={DataTestId.POST_CARD_KEBAB}
                            />
                        </Box>
                    </CardFooter>
                </>
            ) : null}
            {kebab()}
        </Box>
    );
};
